import React from 'react';

export const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <a
            href="https://enter.health"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← enter.health
          </a>
        </div>

        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: January 28, 2026</p>

        <div className="space-y-10 text-sm leading-relaxed">
          <section>
            <h2 className="text-base font-semibold mb-3">1. Who We Are</h2>
            <p className="text-muted-foreground">
              Enter Control ("we", "our", "us") provides an AI-powered browser assistant for
              healthcare professionals, accessible at{' '}
              <a
                href="https://enter.health"
                className="text-foreground underline underline-offset-2"
              >
                enter.health
              </a>{' '}
              and via the Enter Control Chrome extension. This policy explains what data we collect,
              how we use it, and your rights.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">2. Data We Collect</h2>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <p className="font-medium text-foreground mb-1">Account information</p>
                <p>
                  Name, email address, and organization details provided during sign-up or via your
                  identity provider (WorkOS SSO).
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Chat and prompt content</p>
                <p>
                  Messages you send to the AI assistant. These are processed by our AI provider to
                  generate responses and are not used to train AI models.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Voice audio</p>
                <p>
                  When you use voice transcription, audio is captured from your microphone, streamed
                  to our transcription service, converted to text, and then discarded. Audio is
                  never stored.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Usage data</p>
                <p>
                  Anonymized product analytics (feature usage, session counts) to improve the
                  product. Collected via Mixpanel. No personally identifiable health information is
                  included.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Billing information</p>
                <p>
                  Payment processing is handled entirely by Stripe. We do not store credit card
                  numbers or payment details.
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Extension local storage</p>
                <p>
                  User preferences (theme, sidebar state) stored locally in your browser via the
                  Chrome extension storage API. This data never leaves your device.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">3. How We Use Your Data</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>To provide and operate the Enter Control service</li>
              <li>To authenticate your identity and manage your account</li>
              <li>To process subscription payments via Stripe</li>
              <li>To transcribe voice input when you explicitly activate the feature</li>
              <li>To improve product features using anonymized analytics</li>
              <li>To respond to support requests</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We do not sell your data. We do not use your data for advertising.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">4. Data Sharing</h2>
            <div className="space-y-3 text-muted-foreground">
              <p>
                We share data only with the following sub-processors, limited to what is necessary:
              </p>
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-foreground">Provider</th>
                      <th className="text-left px-4 py-2 font-medium text-foreground">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="px-4 py-2">OpenAI</td>
                      <td className="px-4 py-2">AI response generation and voice transcription</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">WorkOS</td>
                      <td className="px-4 py-2">Authentication and SSO</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Stripe</td>
                      <td className="px-4 py-2">Payment processing</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Mixpanel</td>
                      <td className="px-4 py-2">Anonymized product analytics</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Sentry</td>
                      <td className="px-4 py-2">Error monitoring</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">AWS S3</td>
                      <td className="px-4 py-2">File attachment storage</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">5. HIPAA & BAA</h2>
            <p className="text-muted-foreground">
              Enter Control offers a Business Associate Agreement (BAA) for enterprise customers
              subject to HIPAA. If your organization requires a BAA, please contact us at{' '}
              <a
                href="mailto:privacy@enter.health"
                className="text-foreground underline underline-offset-2"
              >
                privacy@enter.health
              </a>
              . Do not enter Protected Health Information (PHI) into the service without an active
              BAA in place.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">6. Data Retention</h2>
            <p className="text-muted-foreground">
              Chat history is retained for as long as your account is active. You can delete
              individual conversations at any time from within the app. Upon account deletion, all
              personal data is removed within 30 days, except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">7. Your Rights</h2>
            <p className="text-muted-foreground mb-3">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing</li>
              <li>Export your data in a portable format</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise any of these rights, email{' '}
              <a
                href="mailto:privacy@enter.health"
                className="text-foreground underline underline-offset-2"
              >
                privacy@enter.health
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">8. Security</h2>
            <p className="text-muted-foreground">
              All data is transmitted over HTTPS. Access to production systems is restricted to
              authorized personnel. We conduct regular security reviews and follow industry best
              practices for data protection.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">9. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this policy from time to time. When we do, we will update the "Last
              updated" date at the top and notify active users by email for material changes.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-3">10. Contact</h2>
            <p className="text-muted-foreground">
              Questions about this policy? Contact us at{' '}
              <a
                href="mailto:privacy@enter.health"
                className="text-foreground underline underline-offset-2"
              >
                privacy@enter.health
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
